import React, { useEffect, useState } from "react";
import { ChatBubbleLeftRightIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { getTodayInsight } from "../api/insightsApi";

// Severity → tailwind classes for the card accent (border, badge background).
const SEVERITY_CLASS = {
  info: {
    border: "border-indigo-500/40",
    badge: "bg-indigo-500/15 text-indigo-500",
    cta: "text-indigo-500",
    bullet: "text-indigo-500",
  },
  warn: {
    border: "border-amber-500/40",
    badge: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    cta: "text-amber-600 dark:text-amber-400",
    bullet: "text-amber-500",
  },
  critical: {
    border: "border-red-500/40",
    badge: "bg-red-500/15 text-red-500",
    cta: "text-red-500",
    bullet: "text-red-500",
  },
};

const SEVERITY_LABEL = {
  info: "TODAY",
  warn: "WATCH",
  critical: "ALERT",
};

const TodayBriefCard = ({ onAsk }) => {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getTodayInsight();
        if (alive) setBrief(data);
      } catch {
        if (alive) setBrief(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading || !brief) return null;

  const sev = SEVERITY_CLASS[brief.severity] || SEVERITY_CLASS.info;
  const sevLabel = SEVERITY_LABEL[brief.severity] || "TODAY";
  const ts = new Date(brief.created_at);

  return (
    <div
      className={`p-5 rounded-2xl bg-white/70 dark:bg-[#1a2332]/60 backdrop-blur-xl border ${sev.border} shadow-glass dark:shadow-glass-dark`}
    >
      <div className="flex items-center gap-3 mb-3">
        <span
          className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${sev.badge}`}
        >
          {sevLabel} · COMPASS
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {ts.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>

      <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-3 leading-snug">
        {brief.title}
      </h3>

      {brief.bullets && brief.bullets.length > 0 ? (
        <ul className="space-y-1.5 mb-4">
          {brief.bullets.map((b, i) => (
            <li
              key={i}
              className="flex gap-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
            >
              <span className={sev.bullet}>•</span>
              <span className="flex-1">{b}</span>
            </li>
          ))}
        </ul>
      ) : (
        brief.body && (
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 whitespace-pre-wrap">
            {brief.body}
          </p>
        )
      )}

      <button
        type="button"
        onClick={() =>
          onAsk?.(
            "Tell me more about today's brief and what I should do about it.",
          )
        }
        className={`inline-flex items-center gap-1.5 text-xs font-semibold ${sev.cta} hover:underline`}
      >
        <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
        Ask the Council why
        <SparklesIcon className="w-3 h-3" />
      </button>
    </div>
  );
};

export default TodayBriefCard;
